import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, X } from 'lucide-react';
import { trackHeroSignupClick } from '../../utils/conversionTracking';

const COLORS = { navy: '#1B2A41', cream: '#FDFBF7', gold: '#F59E0B' };
const DISMISS_KEY = 'sticky_cta_dismissed';

export const StickyCTABar = () => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 700);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const dismiss = () => {
    setDismissed(true);
    sessionStorage.setItem(DISMISS_KEY, '1');
  };

  const shown = visible && !dismissed;

  // Let the global chat floater know to step aside while this bar is on screen (avoids overlap)
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('sticky-cta-bar', { detail: { shown } }));
  }, [shown]);

  return (
    <div
      data-testid="sticky-cta-bar"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: COLORS.navy,
        borderTop: `2px solid ${COLORS.gold}`,
        padding: '0.5rem 2.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.75rem',
        flexWrap: 'wrap',
        zIndex: 9990,
        boxShadow: '0 -6px 20px rgba(0,0,0,0.2)',
        transform: shown ? 'translateY(0)' : 'translateY(100%)',
        opacity: shown ? 1 : 0,
        pointerEvents: shown ? 'auto' : 'none',
        transition: 'transform 0.35s ease, opacity 0.35s ease'
      }}
    >
      <style>{`
        @media (max-width: 640px) {
          [data-testid="sticky-cta-bar"] { padding: 0.45rem 2.25rem 0.45rem 0.9rem !important; flex-wrap: nowrap !important; gap: 0.5rem !important; justify-content: space-between !important; max-height: 56px; }
          [data-testid="sticky-cta-bar"] p { font-size: 0.78rem !important; text-align: left !important; padding: 0 !important; margin: 0 !important; max-width: none !important; line-height: 1.2; }
        }
      `}</style>
      <p style={{ color: COLORS.cream, fontWeight: 600, fontSize: '0.82rem', margin: 0, textAlign: 'center' }}>
        You don't have to do this alone.
      </p>
      <button
        data-testid="sticky-cta-signup-button"
        onClick={() => { trackHeroSignupClick(); navigate('/signup'); }}
        style={{
          background: `linear-gradient(135deg, ${COLORS.gold} 0%, #D97706 100%)`,
          color: COLORS.navy,
          width: 'auto',
          margin: 0,
          minHeight: 'auto',
          padding: '0.4rem 1.1rem',
          borderRadius: '9999px',
          fontWeight: 700,
          fontSize: '0.8rem',
          border: 'none',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.4rem',
          whiteSpace: 'nowrap',
          flexShrink: 0
        }}
      >
        Start Free Today <ArrowRight size={14} />
      </button>
      <button
        data-testid="sticky-cta-dismiss-button"
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          position: 'absolute',
          right: '0.5rem',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'transparent',
          border: 'none',
          color: 'rgba(255,255,255,0.6)',
          cursor: 'pointer',
          width: 'auto',
          margin: 0,
          minHeight: 'auto',
          flexShrink: 0,
          padding: '0.25rem'
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
};
